import React, { useEffect, useState } from 'react';
import { useAcademyStore } from '../../store/academyStore';
import { Terminal, Unlock, Trash2, X } from 'lucide-react';

import { MODULE_SEQUENCE } from '../../data/curriculum';

export const DebugOverlay = () => {
    const { debugMode, toggleDebug, unlockAll, resetProgress, currentUser, logoutUser, progress, settings, toggleVoice } = useAcademyStore();
    // Keyboard Shortcut Listener
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Toggle with Ctrl + Shift + D
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                toggleDebug();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleDebug]);

    const userProgress = currentUser ? progress[currentUser.id] : null;

    const handleLogState = () => {
        console.group('🦍 Academy State Dump');
        console.log('User:', currentUser);
        console.log('Progress:', userProgress);
        console.log('Store:', useAcademyStore.getState());
        console.groupEnd();
        alert('State logged to console (F12)');
    };

    return (
        <>
            {debugMode && (
                <div className="fixed bottom-4 right-4 z-[9999] animate-in slide-in-from-bottom duration-300 print:hidden">
                    <div className="bg-slate-900 text-green-400 rounded-xl shadow-2xl border border-green-500/50 font-mono text-xs w-80 overflow-hidden backdrop-blur-md">
                        {/* Header */}
                        <div className="flex justify-between items-center p-3 bg-green-900/20 border-b border-green-500/30">
                            <div className="flex items-center gap-2 font-bold text-green-300">
                                <Terminal size={14} /> SYSTEM_OVERLAY
                            </div>
                            <button onClick={toggleDebug} className="text-green-500 hover:text-white transition-colors">
                                <X size={16} />
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            {/* User Stats */}
                            <div className="space-y-1 bg-black/40 p-2 rounded border border-green-900/50">
                                <div className="text-[10px] text-green-600 uppercase font-bold tracking-wider">Active User</div>
                                <div className="truncate font-bold text-white">{currentUser?.username || 'Guest'}</div>
                                <div className="flex justify-between text-[10px] text-green-400/80">
                                    <span>XP: {userProgress?.xp || 0}</span>
                                    <span>ID: {currentUser?.id?.slice(0, 6)}...</span>
                                </div>
                                <div className="text-[10px] text-green-400/80">
                                    Modules: {userProgress?.completedModules.length || 0} / {MODULE_SEQUENCE.length}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={unlockAll}
                                    className="col-span-2 bg-green-600/20 hover:bg-green-600/30 text-green-300 border border-green-600/30 px-3 py-2 rounded flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                                >
                                    <Unlock size={14} /> Unlock All
                                </button>

                                <button
                                    onClick={handleLogState}
                                    className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-600/30 px-3 py-2 rounded flex items-center justify-center gap-2 transition-all"
                                >
                                    <Terminal size={14} /> Log State
                                </button>

                                <button
                                    onClick={toggleVoice}
                                    className={`px-3 py-2 rounded flex items-center justify-center gap-2 transition-all border ${settings.voiceEnabled ? 'bg-amber-600/20 text-amber-300 border-amber-600/30' : 'bg-slate-700/50 text-slate-400 border-slate-600'}`}
                                >
                                    {settings.voiceEnabled ? 'Voice ON' : 'Voice OFF'}
                                </button>
                            </div>

                            {/* Danger Zone */}
                            <div className="pt-2 border-t border-red-900/30">
                                <button
                                    onClick={async () => {
                                        if (confirm('⚠️ RESET USER DATA: This will clear progress locally AND on Supabase. Sure?')) {
                                            await resetProgress();
                                            location.reload();
                                        }
                                    }}
                                    className="w-full bg-red-950/30 hover:bg-red-900/50 text-red-400 border border-red-900/50 px-3 py-2 rounded flex items-center justify-center gap-2 transition-colors"
                                >
                                    <Trash2 size={14} /> Clear User Data
                                </button>
                            </div>
                        </div>

                        <div className="bg-black/40 p-1 text-[9px] text-green-800/60 text-center uppercase tracking-widest">
                            Grounded Growth • Top Secret
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
